<template>
  <span class="inline-block">
    <q-btn
      ref="btn"
      dense
      no-caps
      flat
      size="sm"
      icon="link"
      :disable="disable"
      @mousedown="onMouseDown"
      @click="open"
    >
      <q-tooltip>Link</q-tooltip>
    </q-btn>

    <teleport to="body">
      <div
        v-if="contextMenuVisible"
        ref="contextMenu"
        class="link-context-menu bg-white shadow-2 rounded-borders q-py-xs"
        :style="contextMenuStyle"
        @mousedown.prevent
        @click.stop
      >
        <template v-if="existingLink">
          <q-btn
            flat
            dense
            no-caps
            align="left"
            class="full-width q-px-md"
            icon="edit"
            label="Edit Link"
            @click="editFromContext"
          />
          <q-btn
            flat
            dense
            no-caps
            align="left"
            class="full-width q-px-md"
            icon="open_in_new"
            label="Open Link"
            @click="openInNewTab"
          />
          <q-btn
            flat
            dense
            no-caps
            align="left"
            class="full-width q-px-md text-negative"
            icon="link_off"
            label="Remove Link"
            @click="removeFromContext"
          />
        </template>
        <q-btn
          v-else-if="hadSelection"
          flat
          dense
          no-caps
          align="left"
          class="full-width q-px-md"
          icon="link"
          label="Add Link"
          @click="addLinkFromContext"
        />
      </div>
    </teleport>

    <q-dialog v-model="showDialog">
      <q-card style="width: 380px; max-width: 90vw;">
        <q-card-section class="q-pb-none">
          <div class="text-h6">{{ isExistingLink ? 'Edit Link' : 'Add Link' }}</div>
        </q-card-section>
        <q-card-section class="q-gutter-sm">
          <q-input
            v-model="text"
            label="Text"
            dense
            autofocus
            @keyup.enter="apply"
          />
          <q-input
            v-model="url"
            label="URL"
            dense
            @keyup.enter="apply"
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn
            v-if="isExistingLink"
            flat
            label="Remove"
            color="negative"
            @click="remove"
          />
          <q-btn
            flat
            label="Cancel"
            @click="showDialog = false"
          />
          <q-btn
            flat
            color="primary"
            :label="isExistingLink ? 'Update' : 'Add'"
            @click="apply"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </span>
</template>

<script>
export default {
  name: 'LinkBtn',
  props: {
    editor: {
      type: Object,
      default: null
    },
    disable: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showDialog: false,
      url: '',
      text: '',
      existingLink: null,
      hadSelection: false,
      savedRange: null,
      contextMenuVisible: false,
      contextMenuStyle: {}
    }
  },
  computed: {
    isExistingLink () {
      return !!this.existingLink
    }
  },
  watch: {
    editor: {
      immediate: true,
      handler (newEditor, oldEditor) {
        if (oldEditor) {
          this.detachEditorHandlers(oldEditor)
        }
        if (newEditor) {
          this.$nextTick(() => this.attachEditorHandlers(newEditor))
        }
      }
    }
  },
  methods: {
    getRootEl () {
      return this.editor && this.editor.getContentEl ? this.editor.getContentEl() : null
    },
    attachEditorHandlers (editor) {
      const root = editor && editor.getContentEl ? editor.getContentEl() : null
      if (!root) {
        return
      }
      root.addEventListener('click', this.onEditorClick)
      root.addEventListener('contextmenu', this.onEditorContextMenu)
    },
    detachEditorHandlers (editor) {
      const root = editor && editor.getContentEl ? editor.getContentEl() : null
      if (!root) {
        return
      }
      root.removeEventListener('click', this.onEditorClick)
      root.removeEventListener('contextmenu', this.onEditorContextMenu)
    },
    findLinkAncestor (node) {
      const root = this.getRootEl()
      let el = node && node.nodeType === Node.ELEMENT_NODE ? node : (node ? node.parentNode : null)
      while (el && el !== root && el !== document.body) {
        if (el.nodeName === 'A') {
          return el
        }
        el = el.parentNode
      }
      return null
    },
    captureRange () {
      const root = this.getRootEl()
      if (!root) {
        return null
      }
      const sel = document.getSelection()
      if (!sel || sel.rangeCount === 0) {
        return null
      }
      const range = sel.getRangeAt(0)
      if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) {
        return null
      }
      return range.cloneRange()
    },
    showContextMenuFor (link) {
      const rect = link.getBoundingClientRect()
      this.contextMenuStyle = {
        position: 'fixed',
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        zIndex: 9999,
        minWidth: '140px'
      }
      this.existingLink = link
      this.contextMenuVisible = true
    },
    hideContextMenu () {
      this.contextMenuVisible = false
    },
    onEditorClick (e) {
      const link = this.findLinkAncestor(e.target)
      if (link) {
        e.preventDefault()
        this.showContextMenuFor(link)
      }
      else {
        this.hideContextMenu()
      }
    },
    onEditorContextMenu (e) {
      e.preventDefault()

      // Let the browser apply the right-click selection/caret before we read it.
      this.$nextTick(() => {
        const range = this.captureRange()
        this.savedRange = range
        this.hadSelection = !!(range && !range.collapsed)

        const link = this.findLinkAncestor(e.target)
        this.existingLink = link || null

        if (!link && !this.hadSelection) {
          this.hideContextMenu()
          return
        }

        this.contextMenuStyle = {
          position: 'fixed',
          top: `${e.clientY}px`,
          left: `${e.clientX}px`,
          zIndex: 9999,
          minWidth: '160px'
        }
        this.contextMenuVisible = true
      })
    },
    addLinkFromContext () {
      this.hideContextMenu()
      const range = this.savedRange
      const selectedText = range ? range.toString() : ''
      this.text = selectedText
      this.url = /^https?:\/\//i.test(selectedText) ? selectedText : 'https://'
      this.existingLink = null
      this.hadSelection = !!(range && !range.collapsed)
      this.showDialog = true
    },
    onDocumentMouseDown (e) {
      if (!this.contextMenuVisible) {
        return
      }
      const menu = this.$refs.contextMenu
      if (menu && menu.contains(e.target)) {
        return
      }
      const link = this.findLinkAncestor(e.target)
      if (link && this.existingLink && link === this.existingLink) {
        return
      }
      this.hideContextMenu()
    },
    editFromContext () {
      const link = this.existingLink
      if (!link) {
        return
      }
      this.hideContextMenu()

      const range = document.createRange()
      range.selectNodeContents(link)
      this.savedRange = range
      this.hadSelection = true

      this.url = link.getAttribute('href') || ''
      this.text = link.textContent || ''
      this.existingLink = link
      this.showDialog = true
    },
    openInNewTab () {
      const link = this.existingLink
      this.hideContextMenu()
      if (link) {
        const href = link.getAttribute('href')
        if (href) {
          window.open(href, '_blank', 'noopener,noreferrer')
        }
      }
    },
    removeFromContext () {
      const link = this.existingLink
      this.hideContextMenu()
      if (!link) {
        return
      }
      const parent = link.parentNode
      while (link.firstChild) {
        parent.insertBefore(link.firstChild, link)
      }
      parent.removeChild(link)
      this.existingLink = null
      this.notifyEditor()
    },
    onMouseDown (e) {
      e.preventDefault()
      this.savedRange = this.captureRange()
    },
    open () {
      if (!this.editor) {
        return
      }
      let range = this.savedRange
      if (!range && this.editor.caret) {
        const r = this.editor.caret.range
        range = r ? r.cloneRange() : null
      }
      this.savedRange = range

      this.hadSelection = !!(range && !range.collapsed)

      let link = null
      if (range) {
        const startLink = this.findLinkAncestor(range.startContainer)
        const endLink = this.findLinkAncestor(range.endContainer)
        if (startLink && startLink === endLink) {
          link = startLink
        }
      }
      this.existingLink = link

      if (link) {
        this.url = link.getAttribute('href') || ''
        this.text = link.textContent || ''
      }
      else {
        const selectedText = this.hadSelection ? range.toString() : ''
        this.text = selectedText
        this.url = /^https?:\/\//i.test(selectedText) ? selectedText : 'https://'
      }

      this.showDialog = true
    },
    restoreSelection (range) {
      const sel = document.getSelection()
      if (!sel) {
        return
      }
      sel.removeAllRanges()
      sel.addRange(range)
    },
    notifyEditor () {
      const contentEl = this.getRootEl()
      if (contentEl) {
        contentEl.dispatchEvent(new Event('input', { bubbles: true }))
      }
    },
    apply () {
      const url = (this.url || '').trim()
      const text = (this.text || '').trim()
      if (!url || url === 'https://') {
        this.showDialog = false
        return
      }

      if (this.existingLink) {
        const link = this.existingLink
        link.setAttribute('href', url)
        const newText = text || url
        if (link.textContent !== newText) {
          link.textContent = newText
        }

        const after = document.createRange()
        after.setStartAfter(link)
        after.collapse(true)
        this.restoreSelection(after)
        if (this.editor.caret) {
          this.editor.caret.save(after)
        }

        this.notifyEditor()
      }
      else {
        const range = this.savedRange
        if (!range) {
          this.showDialog = false
          return
        }

        if (this.hadSelection) {
          range.deleteContents()
        }

        const a = document.createElement('a')
        a.href = url
        a.textContent = text || url
        range.insertNode(a)

        const after = document.createRange()
        after.setStartAfter(a)
        after.collapse(true)
        this.restoreSelection(after)
        if (this.editor.caret) {
          this.editor.caret.save(after)
        }

        this.notifyEditor()
      }

      this.showDialog = false
      this.existingLink = null
      this.savedRange = null
    },
    remove () {
      if (this.existingLink) {
        const parent = this.existingLink.parentNode
        while (this.existingLink.firstChild) {
          parent.insertBefore(this.existingLink.firstChild, this.existingLink)
        }
        parent.removeChild(this.existingLink)
        this.notifyEditor()
      }
      this.showDialog = false
      this.existingLink = null
      this.savedRange = null
    }
  },
  mounted () {
    document.addEventListener('mousedown', this.onDocumentMouseDown, true)
    document.addEventListener('scroll', this.hideContextMenu, true)
    window.addEventListener('resize', this.hideContextMenu)
  },
  beforeUnmount () {
    document.removeEventListener('mousedown', this.onDocumentMouseDown, true)
    document.removeEventListener('scroll', this.hideContextMenu, true)
    window.removeEventListener('resize', this.hideContextMenu)
    if (this.editor) {
      this.detachEditorHandlers(this.editor)
    }
  }
}
</script>

<style scoped>
.link-context-menu {
  display: flex;
  flex-direction: column;
  border: 1px solid #e0e0e0;
}
</style>
